import React, { useEffect, useState, useRef } from "react";
import { getStyles } from "../../../assets/js/utils/functions";
import { getItemThumbnail, getSizedImage } from "../../../assets/js/utils/utils";
import useScreenSize, { BreakPoint } from "../../hooks/useScreenSize";
import useSlider from "../../hooks/useSlider";
import LazyImage from "../../lazy-image";
import { AppBtn } from "../../ui/buttons";
import AnimatedHeaderText from "../../front-pages/commons/animated-header-text";
import { useRequest } from "@/api/utils";
import { StoreCategory, StoreInterface } from "@/assets/interfaces";
import CategoryLoader from "../items/category-loader";
import Router from "next/router";
import { GetStoreCategoriesWithItems } from "@/api/stores";
import StorefrontContext from "@/contexts/storefront";

interface Props {
  store: StoreInterface;
}

const StoreCategories: React.FC<Props> = ({ store }) => {
  const { screen } = useScreenSize();
  const { paths } = StorefrontContext.useContainer();

  const { categoriesWithItems, isLoadingCategories } = StorefrontContext.useContainer();
  const categories =
    store?.configuration?.top_category_cards !== false
      ? categoriesWithItems.slice(1, categoriesWithItems.length)
      : categoriesWithItems;
  const { next, slider, slide, previous, slides, currentSlide, switchSlides, hasNext, hasPrevious } = useSlider({
    slides: categories?.length,
    sameWidthAsSlider: false,
    gap: 24,
  });

  // Array of refs for each slide
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const scrollbarTrack = useRef<HTMLDivElement | null>(null);
  const scrollbarHandle = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Calculate the number of visible items based on screen size
  const getVisibleItems = () => {
    if (screen === BreakPoint.xs) return 2; // 48% width
    if (screen === BreakPoint.sm) return 3; // 38% width
    if (screen === BreakPoint.m) return 4; // 29% width
    return 5; // 20% width (lg and above)
  };

  const visibleItems = getVisibleItems();

  // Calculate the approximate item width including the gap
  const itemWidth = slider.current ? slider.current.offsetWidth / visibleItems : 0;

  // Override next and previous to use scrollTo with smooth behavior as a fallback
  const handleNext = () => {
    if (slider.current && hasNext && currentSlide < slides - visibleItems) {
      const newScrollLeft = (currentSlide + 1) * (itemWidth + 24); // 24 is the gap
      slider.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
      next();
    }
  };

  const handlePrevious = () => {
    if (slider.current && hasPrevious) {
      const newScrollLeft = (currentSlide - 1) * (itemWidth + 24); // 24 is the gap
      slider.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
      previous(); // Update the currentSlide in the hook
    }
  };

  // Log scrollLeft changes to debug SmoothHorizontalScrolling
  useEffect(() => {
    const sliderEl = slider.current;
    if (sliderEl) {
      const handleScroll = () => {
        // console.log("scrollLeft: ", sliderEl.scrollLeft);
      };
      sliderEl.addEventListener("scroll", handleScroll);
      return () => sliderEl.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Update scrollbar handle position based on slider scroll
  const updateScrollbar = () => {
    if (slider.current && scrollbarHandle.current && scrollbarTrack.current) {
      const { scrollLeft, scrollWidth, clientWidth } = slider.current;
      const trackWidth = scrollbarTrack.current.offsetWidth;
      const handleWidth = (clientWidth / scrollWidth) * trackWidth;
      const handleLeft = (scrollLeft / scrollWidth) * trackWidth;

      scrollbarHandle.current.style.width = `${handleWidth}px`;
      scrollbarHandle.current.style.transform = `translateX(${handleLeft}px)`;
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX); // Use the first touch point
    if (slider.current) {
      setScrollLeft(slider.current.scrollLeft);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !slider.current || !scrollbarTrack.current) return;

    const dx = e.touches[0].pageX - startX; // Calculate the horizontal movement
    const { scrollWidth, clientWidth } = slider.current;
    const trackWidth = scrollbarTrack.current.offsetWidth;
    const scrollWidthRatio = scrollWidth / trackWidth;

    slider.current.scrollLeft = Math.min(Math.max(scrollLeft + dx * scrollWidthRatio, 0), scrollWidth - clientWidth);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const sliderEl = slider.current;
    if (sliderEl) {
      sliderEl.addEventListener("scroll", updateScrollbar);
      return () => sliderEl.removeEventListener("scroll", updateScrollbar);
    }
  }, []);

  useEffect(() => {
    updateScrollbar(); // Initialize scrollbar position on mount
  }, [categories]);

  if (categories.length === 0 && !isLoadingCategories) return null;

  return (
    <section className="py-3 mb-7">
      <div className="w-full block space-y-6 sm:space-y-8 md:space-y-15 px-5 sm:px-9 md:px-12.5">
        <div className="store-container flex justify-between items-center mb-5 sm:mb-5.5 lg:mb-6.5">
          <h3 className="text-black text-lg sm:text-xl md:text-xl font-bold">Categories</h3>

          <div className="hidden sm:flex items-center space-x-3">
            <button
              className={`h-6.25 sm:h-7.5 w-8 sm:w-10 rounded-15 flex items-center justify-center bg-grey-fields-100 text-primary-500`}
              onClick={handlePrevious}
              aria-label="previous"
            >
              {/* prettier-ignore */}
              <svg width="20" viewBox="0 0 25 25" fill="none" style={{ transform: "rotate(180deg)" }}>
                <path d="M5.95703 12.3396H19.957" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12.957 5.3396L19.957 12.3396L12.957 19.3396" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button
              className={`h-6.25 sm:h-7.5 w-8 sm:w-10 rounded-15 flex items-center justify-center bg-grey-fields-100 text-primary-500`}
              onClick={handleNext}
              aria-label="next"
            >
              {/* prettier-ignore */}
              <svg width="20" viewBox="0 0 25 25" fill="none" >
                <path d="M5.95703 12.3396H19.957" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12.957 5.3396L19.957 12.3396L12.957 19.3396" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="w-full block space-y-6 sm:space-y-8 md:space-y-15">
        <div className="relative left-0 right-0 w-screen">
          <ul className="flex overflow-x-auto scrollbar-hide gap-6 md:gap-8 carousel-responsive-padding" ref={slider}>
            {categories && categories.length > 0
              ? categories.map((i, index) => (
                  <article
                    key={index}
                    onClick={() => Router.push(`${paths.products}?category=${i.id}`)}
                    ref={(el) => (slideRefs.current[index] = el as HTMLDivElement | null)} // Assign ref to each slide
                    className="flex-shrink-0 w-[40%] sm:w-[38%] md:w-[29%] lg:w-[20%] pt-[45%] md:pt-[35%] lg:pt-[25%] relative item-card overflow-hidden rounded-15 sell-cat-item group"
                    style={getStyles({ "--delay": `${index * 0.2}s`, "--from": "15%", "--duration": "1s" })}
                  >
                    <figure className="absolute top-0 left-0 h-full w-full rounded-15 safari-overflow-fix transition-transform duration-300 ease-out">
                      <LazyImage
                        src={i?.first_item ? getItemThumbnail(i?.first_item) : ""}
                        alt={i.name}
                        className="h-full w-full object-cover rounded-15 group-hover:scale-105 will-change-transform backface-visibility-hidden"
                      />
                    </figure>
                    <div className="w-full h-[100%] absolute top-0 store-cat-overlay left-0 flex flex-col justify-center items-center px-4.5 md:px-5 text-center text-md sm:text-1sm lg:text-lg">
                      <span className="text-white font-bold !leading-none overflow-hidden overflow-ellipsis max-w-[90%]">{i.name}</span>
                      <button
                        type="button"
                        className="absolute rounded-full top-4 right-4 p-3 bg-white text-primary-500 opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100 transform group-hover:rotate-[320deg]"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M6 0L4.9425 1.0575L9.1275 5.25H0V6.75H9.1275L4.9425 10.9425L6 12L12 6L6 0Z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                    </div>
                  </article>
                ))
              : Array.from(Array(6).keys()).map((_, index) => <CategoryLoader key={index} />)}
          </ul>
        </div>
      </div>

      <div
        ref={scrollbarTrack}
        className="relative mt-4.5 h-1 bg-grey-fields-200 mx-5 sm:mx-9 md:mx-12.5 sm:hidden"
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={scrollbarHandle}
          className="absolute top-0 left-0 h-full bg-black-muted cursor-pointer"
          onTouchStart={handleTouchStart}
          style={{ width: "2%" }}
        ></div>
      </div>
    </section>
  );
};

export default StoreCategories;
